import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Wordmark } from './Wordmark';

interface NavItem {
  label: string;
  to: string;
}

// Contact intentionally uses a hash anchor (not a route) so it renders as a
// plain <a> and never participates in active-dot matching. This keeps the
// product routes (/indication, /property) as the only "active" states.
const NAV_ITEMS: NavItem[] = [
  { label: 'Builders Risk Indication', to: '/indication' },
  { label: 'Property Insurance Quote', to: '/property' },
];

const CONTACT_HREF = '/#contact';

function NavDot({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={clsx(
        'inline-block w-[6px] h-[6px] rounded-full transition-all duration-200',
        active
          ? 'bg-gradient-primary'
          : 'border border-ink-subtle bg-transparent',
      )}
    />
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close sheet on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className="sticky top-0 z-50 bg-canvas/80 backdrop-blur-md border-b border-hairline">
      <div className="container-site flex items-center justify-between h-16">
        <Wordmark size="sm" />

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2 text-sm font-display transition-colors duration-200',
                  isActive
                    ? 'text-ink underline underline-offset-4 decoration-1'
                    : 'text-ink-muted hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <NavDot active={isActive} />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
          <a
            href={CONTACT_HREF}
            className="flex items-center gap-2 text-sm font-display text-ink-muted hover:text-ink transition-colors duration-200"
          >
            <NavDot active={false} />
            <span>Contact</span>
          </a>
        </nav>

        <button
          type="button"
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden relative w-10 h-10 flex items-center justify-center"
        >
          <span
            className={clsx(
              'absolute block w-5 h-[1.5px] bg-ink transition-transform duration-200',
              open ? 'rotate-45' : '-translate-y-[5px]',
            )}
          />
          <span
            className={clsx(
              'absolute block w-5 h-[1.5px] bg-ink transition-opacity duration-200',
              open ? 'opacity-0' : 'opacity-100',
            )}
          />
          <span
            className={clsx(
              'absolute block w-5 h-[1.5px] bg-ink transition-transform duration-200',
              open ? '-rotate-45' : 'translate-y-[5px]',
            )}
          />
        </button>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          className="md:hidden bg-canvas/95 backdrop-blur-md border-b border-hairline"
          aria-label="Mobile"
        >
          <ul className="container-site flex flex-col gap-1 py-4">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 py-3 text-[0.9375rem] font-display',
                      isActive ? 'text-ink' : 'text-ink-muted',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <NavDot active={isActive} />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
            <li>
              <a
                href={CONTACT_HREF}
                className="flex items-center gap-3 py-3 text-[0.9375rem] font-display text-ink-muted"
              >
                <NavDot active={false} />
                <span>Contact</span>
              </a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}
